import React, { useCallback, useEffect } from "react";
import { ITEMS_PER_PAGE, useHotels } from "@/lib/accommodation/agodaHotels";
import { HotelInitial } from "@/types";
import LoadingCard from "./LoadingCard";
import HotelCard from "./HotelCard";
import useTransportStore from "@/stores/tripBuilder/useTransport";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import useCurrencyStore from "@/stores/ui/currencyStore";
import { BedDouble, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import useAuthStore from "@/stores/user/authStore";
import { useRateLimitStore } from "@/stores/rateLimitStore";

interface HotelsProps {
  checkIn: string;
  checkOut: string;
  handleAddToTrip: (selectedHotel: HotelInitial) => void;
  handleModalOpenMainLodging: (viewLodging: HotelInitial) => void;
  isAdmin: boolean;
}

const Hotels: React.FC<HotelsProps> = ({
  checkIn,
  checkOut,
  handleAddToTrip,
  handleModalOpenMainLodging,
  isAdmin,
}) => {
  const selectedCurrency = useCurrencyStore((state) => state.selectedCurrency);
  const accommodationBounds = useTransportStore(
    (state) => state.accommodationBounds
  );
  const anonymousUser = useAuthStore((state) => state.anonymousUser);
  const setRateLimit = useRateLimitStore((state) => state.setRateLimit);
  const setResponseHotels = useAccommodationStore(
    (state) => state.setResponseHotels
  );
  const setIsFetchingHotels = useAccommodationStore(
    (state) => state.setIsFetchingHotels
  );
  const currentPage = useAccommodationStore((state) => state.currentPage);
  const setCurrentPage = useAccommodationStore((state) => state.setCurrentPage);
  const selectedRating = useAccommodationStore((state) => state.selectedRating);
  const selectedPriceRange = useAccommodationStore(
    (state) => state.selectedPriceRange
  );

  const { data, error, isLoading, isFetching, status, refetch } = useHotels(
    accommodationBounds,
    checkIn,
    checkOut,
    currentPage,
    selectedCurrency.code,
    {
      minRating: selectedRating ?? undefined,
      priceRange: selectedPriceRange ?? undefined,
    }
  );

  useEffect(() => {
    if (error) {
      console.error("Error fetching hotels:", error);
      setIsFetchingHotels(false);
    } else {
      setIsFetchingHotels(isLoading);
    }
  }, [isLoading, setIsFetchingHotels, error]);

  // useEffect(() => {
  //   if (shouldSearchArea) {
  //     refetch();
  //     setShouldSearchArea(false);
  //   }
  // }, [shouldSearchArea, refetch, setShouldSearchArea]);

  useEffect(() => {
    if (data) {
      setResponseHotels(data.data);
    }
  }, [data, setResponseHotels]);

  const handleNextPage = () => {
    if (anonymousUser) {
      setRateLimit("Create your account to view more lodging options!");
      return;
    }
    if (data?.nextCursor !== null && data?.nextCursor !== undefined) {
      setCurrentPage(data.nextCursor);
    }
  };

  const handlePrevPage = () => {
    if (currentPage >= ITEMS_PER_PAGE) {
      setCurrentPage(currentPage - ITEMS_PER_PAGE);
    }
  };

  return (
    <div className="flex flex-col w-full min-h-full">
      <div className="hotels-list w-full mt-2 rounded-lg overflow-y-scroll no-scrollbar h-[calc(100vh-140px)]">
        {status === "pending" || isFetching ? (
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
            {Array.from({ length: 8 }, (_, i) => i).map((_, index) => (
              <LoadingCard key={index} />
            ))}
          </div>
        ) : data?.data.length === 0 ? (
          <div className="flex flex-col min-h-full items-center justify-center p-8 text-center bg-neutral-50 rounded-md">
            <BedDouble className="w-8 h-8 text-neutral-300 mb-4" />
            <p className="text-lg text-neutral-600">No hotels found</p>
            <p className="text-sm font-light text-neutral-400 mt-1">
              Try expanding your search radius
            </p>
          </div>
        ) : (
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
            {data?.data.map((listing: HotelInitial) => (
              <HotelCard
                key={listing.hotelId}
                listing={listing}
                handleAddToTrip={handleAddToTrip}
                handleModalOpenMainLodging={handleModalOpenMainLodging}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        )}
        {data?.data.length && data?.data.length > 0 && (
          <div className="flex justify-center items-center gap-4 py-4 mt-2 bg-white border-t">
            <Button
              variant="outline"
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className="flex items-center justify-center gap-1.5 rounded-full font-light text-sm px-3 py-1.5 hover:bg-neutral-100 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-neutral-500 font-light px-2">
              Page {Math.floor(currentPage / ITEMS_PER_PAGE) + 1}
            </span>
            <Button
              variant="outline"
              onClick={handleNextPage}
              disabled={data?.nextCursor === null}
              className="flex items-center justify-center gap-1.5 rounded-full font-light text-sm px-3 py-1.5 hover:bg-neutral-100 transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Hotels;
